/**
 * Supplier KPIs Component
 *
 * Displays key performance indicators for suppliers.
 */

import { Building2, CheckCircle, Package } from 'lucide-react';
import { Card, Skeleton } from '@/components/ui';
import { useSupplierKPIs } from '../hooks/useSuppliers';

interface KPICardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'primary' | 'success' | 'warning';
}

function KPICard({ icon, label, value, color }: KPICardProps) {
  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600',
    success: 'bg-success-100 text-success-600',
    warning: 'bg-warning-100 text-warning-600',
  };

  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-neutral-500">{label}</p>
          <p className="text-2xl font-semibold text-neutral-900">{value}</p>
        </div>
      </div>
    </Card>
  );
}

export function SupplierKPIs() {
  const { data: kpis, isLoading } = useSupplierKPIs();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
    );
  }

  if (!kpis) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <KPICard
        icon={<Building2 className="w-5 h-5" />}
        label="Fournisseurs"
        value={kpis.totalSuppliers}
        color="primary"
      />
      <KPICard
        icon={<CheckCircle className="w-5 h-5" />}
        label="Actifs"
        value={kpis.activeSuppliers}
        color="success"
      />
      <KPICard
        icon={<Package className="w-5 h-5" />}
        label="References catalogue"
        value={kpis.totalCatalogItems}
        color="warning"
      />
    </div>
  );
}
